import { useEffect, useState } from "react";
import Modal from "./Modal";
import { apiGet } from "src/utils/api";
import Loading from "../Loading";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

interface ApiDocModalProps {
  onClose: () => void;
  org_id: string;
}

const ApiDocModal = ({ onClose, org_id }: ApiDocModalProps) => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const response = await apiGet(`/api/${org_id}/docs/api-key`);
        const data = await response.json();
        setContent(data.content);
      } catch (err) {
        setError("Failed to load API documentation");
      } finally {
        setLoading(false);
      }
    };
    fetchDocs();
  }, [ org_id ]);

  return (
    <Modal title="API Documentation" onClose={onClose}>
      {loading ? (
        <Loading message="Loading documentation..." />
      ) : error ? (
        <div className="text-red-600 text-center py-8">{error}</div>
      ) : (
        <div className="prose prose-sm max-w-none prose-slate">
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="text-2xl font-bold text-gray-900 border-b border-gray-200 pb-3 mb-4">
                  {children}
                </h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-xl font-semibold text-gray-800 mt-6 mb-3">
                  {children}
                </h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-lg font-semibold text-gray-700 mt-4 mb-2">
                  {children}
                </h3>
              ),
              h4: ({ children }) => (
                <h4 className="text-base font-semibold text-gray-700 mt-3 mb-2">
                  {children}
                </h4>
              ),
              p: ({ children }) => (
                <p className="text-gray-600 mb-3 leading-relaxed">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="list-disc list-inside text-gray-600 mb-3 space-y-1">
                  {children}
                </ul>
              ),
              li: ({ children }) => (
                <li className="text-gray-600">{children}</li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-gray-800">{children}</strong>
              ),
              hr: () => <hr className="border-gray-200 my-6" />,
              code: ({ className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || "");
                const isInline = !match;

                if (isInline) {
                  return (
                    <code
                      className="bg-gray-100 text-rose-600 px-1.5 py-0.5 rounded text-sm font-mono"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                }

                return (
                  <SyntaxHighlighter
                    style={oneDark}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{
                      borderRadius: "0.5rem",
                      fontSize: "0.8rem",
                      margin: "1rem 0",
                    }}
                  >
                    {String(children).replace(/\n$/, "")}
                  </SyntaxHighlighter>
                );
              },
              pre: ({ children }) => <>{children}</>,
            }}
          >
            {content || ""}
          </ReactMarkdown>
        </div>
      )}
    </Modal>
  );
};

export default ApiDocModal;

